import { Divider, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router';
import { useStore } from '../../hooks/useStore';
import { Loading, Page, PageWithMenu, RoundedPaper } from '../../lib/components';
import { companySubmenuConfig } from '../../submenus/company';
import { useSnackbar } from 'notistack';
import { CombinedSupportMessage } from '../../lib/components/compound/feedback/combinedSupportMessage';
import { GocardlessInvoiceDialog } from '../../lib/components/compound/dialogs/gocardlessInvoiceDialog';

const Company = () => {
  const { t } = useTranslation();
  const { companyStore } = useStore();
  const snackbar = useSnackbar();

  const [showGocardlessDialog, setShowGocardlessDialog] = useState(false);

  const navigateToInvoicePortal = async () => {
    try {
      await companyStore.openInvoicePortal();
    } catch {
      snackbar.enqueueSnackbar({
        message: <CombinedSupportMessage message={t('company:FAILED_STRIPE_BILLING_NAVIGATION')} />,
        variant: 'error'
      });
    }
  };

  const handleOpenInvoicePortal = () => {
    if (companyStore.usesGocardlessDirectDebit) {
      setShowGocardlessDialog(true);
      return;
    }
    navigateToInvoicePortal();
  };

  const isBonusStandalone = companyStore.company?.is_bonus_standalone ?? false;

  const [menuConfig, setMenuConfig] = useState(
    companySubmenuConfig(
      t,
      1,
      1,
      1,
      1,
      handleOpenInvoicePortal,
      undefined,
      undefined,
      isBonusStandalone
    )
  );
  const [isLoading, setIsLoading] = useState(true);

  const companyAddress = useMemo(() => {
    const address = companyStore.generalData?.address;
    const street = `${[address?.street_name, address?.street_number]
      .filter(Boolean)
      .join('\u00A0')}`;
    const city = `${[address?.zip_code, address?.city].filter(Boolean).join('\u00A0')}`;
    return `${[street, city].filter(Boolean).join(', ')}`;
  }, [companyStore.generalData]);

  useEffect(() => {
    let isCanceled = false;

    const fetch = async () => {
      if (!isCanceled && companyStore.company) {
        setIsLoading(true);
        const locations = companyStore.fetchLocations();
        const departments = companyStore.fetchDepartments();
        const costCenters = companyStore.fetchCostCenters();
        const groups = companyStore.fetchGroups();
        const benefitSettings = companyStore.fetchBenefitSettings();
        const settings = companyStore.fetchCompanySettings();
        const paymentData = companyStore.fetchPaymentData();

        await Promise.all([
          locations,
          departments,
          costCenters,
          groups,
          benefitSettings,
          settings,
          paymentData
        ]);
        setMenuConfig(
          companySubmenuConfig(
            t,
            companyStore.locations?.length ?? 0,
            companyStore.departments?.length ?? 0,
            companyStore.costCenters?.length ?? 0,
            companyStore.groups?.length ?? 0,
            handleOpenInvoicePortal,
            (companyStore.settings?.hris_integration_status &&
              ['CONNECTION_COMPLETE', 'PAUSED'].includes(
                companyStore.settings?.hris_integration_status
              )) ??
              false,
            companyStore.featureEnabled('COMPANY_LOGO'),
            isBonusStandalone
          )
        );
        setIsLoading(false);
      }
    };

    fetch();
    return () => {
      isCanceled = true;
    };
  }, [companyStore.company?.id]);

  return isLoading ? (
    <Page>
      <Loading />
    </Page>
  ) : (
    <PageWithMenu
      menuConfig={menuConfig}
      elementAboveMenu={
        <RoundedPaper sx={{ padding: 2.5 }}>
          <Typography variant="h3" paddingBottom={1}>
            {companyStore.company?.name}
          </Typography>
          <Divider />
          <Typography paddingTop={1}>{companyAddress}</Typography>
        </RoundedPaper>
      }
    >
      {isLoading ? <Loading /> : <Outlet />}
      <GocardlessInvoiceDialog
        open={showGocardlessDialog}
        onConfirm={navigateToInvoicePortal}
        onClose={() => setShowGocardlessDialog(false)}
      />
    </PageWithMenu>
  );
};

export default observer(Company);
